/**
 * Table Settings Preferences API Service
 *
 * Provides read/write operations for persisting user table column settings
 * (visibility and order) per tab and section via the backend API.
 *
 * ────────────────────────────────────────────────────────────────────
 * API ENDPOINTS
 * ────────────────────────────────────────────────────────────────────
 *
 * GET  /users/user?username=<email>
 *   → Returns user object including seMetricsTableSettings field.
 *   seMetricsTableSettings structure:
 *   {
 *     "COMMERCIAL_AND_CONSUMER": {
 *       "ENGINEERING_PERFORMANCE_KPIS": ["COL_API_ID", ...],
 *       ...
 *     }
 *   }
 *   Array contains visible column API IDs in display order.
 *
 * POST /users/{userId}/se-metrics/table-settings
 *   → Save table settings for the user.
 *   Request body:
 *   {
 *     "seMetricsTableSettings": {
 *       "<DEPT_API_KEY>": {
 *         "<TAB_API_KEY>": ["COL_API_ID", ...]
 *       }
 *     }
 *   }
 *
 * NOTES:
 *  - userId is obtained from the user object (fetchUserInfo).
 *  - columnVisibility only includes *configurable* columns.
 *    Fixed columns (program, status, rowHoverActions) are never stored.
 *  - The API array stores only visible columns in display order.
 *  - preferenceKey format: `${tabId}__${departmentSlug}`
 *    e.g. "engineering-performance__commercial-and-consumer"
 * ────────────────────────────────────────────────────────────────────
 */

import {post} from './apiClient'
import {ENDPOINTS} from '@/utils/constants/apiEndpoints'
import {apiResponseToReduxState, reduxStateToApiPayload} from './tableSettingsMapper'
import type {TableColumnSettings} from '@/store/slices/tableSettingsSlice'

export type {TableColumnSettings}

// Re-export the Redux interface for consumers that import from this file
export interface UserTablePreferences {
  preferences: {
    seMetrics: Record<string, TableColumnSettings>
  }
}

// ─── API Implementation ─────────────────────────────────────────────

/**
 * Parse table settings from the user API response's seMetricsTableSettings field.
 * Converts the API format to Redux-compatible state.
 *
 * @param apiSettings - The seMetricsTableSettings object from GET /users/user response
 * @returns Redux-compatible settings keyed by preferenceKey
 */
export const parseTableSettingsFromUserResponse = (
  apiSettings: Record<string, Record<string, string[]>> | null | undefined,
): Record<string, TableColumnSettings> => {
  if (!apiSettings || typeof apiSettings !== 'object') return {}
  return apiResponseToReduxState(apiSettings)
}

/**
 * Save or update settings for a single table view via the backend API.
 *
 * POST /users/{userId}/se-metrics/table-settings
 */
export const saveTableSettings = async (
  userId: string,
  preferenceKey: string,
  columnVisibility: Record<string, boolean>,
  columnOrder: string[],
): Promise<{success: boolean; updatedAt: string}> => {
  try {
    const payload = reduxStateToApiPayload(preferenceKey, columnVisibility, columnOrder)
    if (!payload) {
      console.warn('[TableSettings] Could not map preferenceKey to API keys:', preferenceKey)
      return {success: false, updatedAt: ''}
    }

    const endpoint = ENDPOINTS.USER_TABLE_SETTINGS.replace('{userId}', userId)

    await post(endpoint, {
      data: {
        // seMetricsTableSettings: {
        [payload.deptApiKey]: {
          [payload.tabApiKey]: payload.columns,
        },
        // },
      },
    })

    return {success: true, updatedAt: new Date().toISOString()}
  } catch (error) {
    console.error('[TableSettings] Error saving settings:', error)
    return {success: false, updatedAt: ''}
  }
}

// ─── Preference Key Helpers ─────────────────────────────────────────

/**
 * Builds a standardised preference key from tab and section identifiers.
 * @example buildPreferenceKey('engineering-performance', 'Commercial & Consumer')
 *   → 'engineering-performance__commercial-and-consumer'
 */
export const buildPreferenceKey = (tabId: string, sectionId: string): string => {
  const normalise = (s: string) => s.toLowerCase().replace(/&/g, '-').replace(/\s+/g, '-').replace(/-{2,}/g, '-')
  return `${normalise(tabId)}__${normalise(sectionId)}`
}
